import React, { useRef, useState } from 'react';
import EditorCuerpo, { opsVacias } from './EditorCuerpo';
import BotonesPlantilla from './BotonesPlantilla';
import { EMAIL_TEMPLATE_VARIABLES } from '@claimup/shared/constants/email-templates';
import PreviaDePlantilla from '../../../../components/template-preview/PreviaDePlantilla';
import { textoDeOps } from '../../../../components/template-preview/partirPorVariables';

/**
 * Escribir una plantilla de correo desde la pantalla.
 *
 * Hasta ahora el catálogo **solo podía nacer de un script**: la API tenía
 * `PATCH` para editar una plantilla existente, pero ninguna forma de crear.
 * Quien quisiera una plantilla nueva tenía que entrar en un terminal.
 *
 * **No se pide la clave.** Se deriva del título en el backend, que es el único
 * sitio donde puede garantizarse que no choca. Una clave es un detalle técnico
 * que une la definición con las copias de cada cliente; quien escribe un correo
 * no tiene por qué saber que existe.
 *
 * **El cuerpo se escribe con formato.** Lo que se guarda no es HTML sino la
 * estructura del editor: así el HTML del correo y su versión en texto plano
 * salen los dos de lo mismo, y no hay HTML de fuera que sanear en el servidor.
 *
 * **Las variables se insertan, no se escriben.** Botón a botón, para que nadie
 * tenga que acordarse de si era `{{payment_url}}` o `{{urlPago}}` — el backend
 * rechaza las que no conoce, pero mejor no llegar al error.
 */
const NuevaPlantilla = ({ onCrear, onCancelar, whatsapp, empresa, color }) => {
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyOps, setBodyOps] = useState([]);
  const [guardando, setGuardando] = useState(false);
  // Dónde se insertará la siguiente variable: el campo que se tocó el último.
  const [ultimoCampo, setUltimoCampo] = useState('body');
  const editorRef = useRef(null);

  const listo = description.trim() && subject.trim() && !opsVacias(bodyOps);

  const insertar = nombre => {
    // En el cuerpo va donde está el cursor, no al final: aquí se escribe un
    // correo entero y pegarlo abajo obligaría a cortar y pegar cada vez.
    if (ultimoCampo === 'subject') setSubject(t => `${t}{{${nombre}}}`);
    else editorRef.current?.insertarVariable(nombre);
  };

  const enviar = async e => {
    e.preventDefault();
    setGuardando(true);
    try {
      await onCrear({
        description: description.trim(),
        subject: subject.trim(),
        body_ops: bodyOps,
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form
      onSubmit={enviar}
      aria-label='Nueva plantilla'
      className='bg-white border border-blue-200 rounded-lg p-4 space-y-3'
    >
      <p className='text-sm font-medium text-slate-800'>Nueva plantilla</p>

      <label className='block text-xs font-medium text-slate-600'>
        Título
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder='Aviso antes de pasar a judicial'
          className='mt-1 block w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none'
        />
        <span className='block text-xs font-normal text-slate-400 mt-0.5'>
          Para identificarla aquí dentro. El deudor no lo ve.
        </span>
      </label>

      <label className='block text-xs font-medium text-slate-600'>
        Asunto
        <input
          value={subject}
          onFocus={() => setUltimoCampo('subject')}
          onChange={e => setSubject(e.target.value)}
          placeholder='Factura {{debt_title}} pendiente'
          className='mt-1 block w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none'
        />
      </label>

      <div className='block text-xs font-medium text-slate-600'>
        Cuerpo
        <div className='mt-1'>
          <EditorCuerpo
            ref={editorRef}
            onChange={setBodyOps}
            alEnfocar={() => setUltimoCampo('body')}
            // Para que el botón que se inserte se vea con el color de la
            // empresa, que es el que va a salir en el correo.
            color={color}
          />
        </div>
      </div>

      {/* Debajo del cuerpo, no arriba: se lee después de escribir, como se
          relee un mensaje antes de mandarlo. */}
      <PreviaDePlantilla asunto={subject} cuerpo={textoDeOps(bodyOps)} companyId={empresa} />

      <BotonesPlantilla
        onInsertar={boton => editorRef.current?.insertarBoton(boton)}
        whatsapp={whatsapp}
      />

      <div className='flex flex-wrap items-center gap-1.5'>
        <span className='text-xs text-slate-500'>
          Insertar en {ultimoCampo === 'subject' ? 'el asunto' : 'el cuerpo'}:
        </span>
        {Object.entries(EMAIL_TEMPLATE_VARIABLES).map(([nombre, que]) => (
          <button
            key={nombre}
            type='button'
            title={que}
            onClick={() => insertar(nombre)}
            className='rounded-lg border border-slate-200 px-2 py-1 font-mono text-xs text-slate-600 hover:bg-slate-50'
          >
            {nombre}
          </button>
        ))}
      </div>

      <div className='flex items-center gap-2'>
        <button
          type='submit'
          disabled={!listo || guardando}
          className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium ${
            !listo || guardando ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {guardando ? 'Creando…' : 'Crear plantilla'}
        </button>
        <button
          type='button'
          onClick={onCancelar}
          disabled={guardando}
          className='text-xs text-slate-500 hover:text-slate-700'
        >
          Cancelar
        </button>
      </div>
    </form>
  );
};

export default NuevaPlantilla;
